/**
 * Analytics Portal V1 — the shared single-entity dashboard body (Phase 12).
 *
 * Extracted from the Phase 11 Profile tab so Profile and Business (and later Capital, Events and
 * Resources) share one renderer instead of copied markup. It is a pure renderer of an entity
 * dashboard payload: every value, comparison, availability flag, notice, funnel stage, traffic
 * source and audience row comes from the server, and no metric arithmetic happens here.
 * Entity selection, range, comparison, timezone, refresh, freshness and the notice stack all live
 * in the Phase 9 shell.
 */
import React from "react";
import { AudienceBreakdown } from "./AudienceBreakdown";
import { AvailabilityNotice } from "./AvailabilityNotice";
import { EmptyState } from "./EmptyState";
import { FunnelChart } from "./FunnelChart";
import { KpiCard } from "./KpiCard";
import { KpiGroup } from "./KpiGroup";
import { TrafficSourceChart } from "./TrafficSourceChart";
import { TrendChart } from "./TrendChart";

export const EntityDashboard = ({ portal, testId, scopeType, headline = [], groups = [],
                                  funnelTitle, noActivityBody, beforeGroups, afterGroups }) => {
  const { payload, entities = [], entity, loading } = portal || {};

  if (!loading && entities.length === 0) {
    return <div data-testid={testId}><EmptyState state="no-entities" /></div>;
  }
  if (!entity || !payload) {
    return <div data-testid={testId}><EmptyState state="processing" /></div>;
  }
  // `never_processed` is a FRESHNESS state, not an access state: the payload still carries the
  // server's supported zero counters, funnel, series and suppression notices, so the dashboard
  // renders in full with an honest notice on top instead of being replaced by an empty state.
  const neverProcessed = payload.freshness?.status === "never_processed";

  const kpis = payload.kpis || {};
  const cards = headline.filter((item) => kpis[item.key]);

  return (
    <div className="space-y-7" data-testid={testId}>
      <div data-testid="analytics-dashboard-body" className="space-y-7">
        {neverProcessed && (
          <AvailabilityNotice testId="analytics-not-processed-notice" reason="never_processed"
                              notice={noActivityBody
                                || "Analytics for this entity will populate once eligible activity is collected."} />
        )}

        <section className="space-y-3" aria-label="Headline metrics">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {cards.map((item) => (
              <KpiCard key={item.key} metricKey={item.key} label={item.label} hint={item.hint}
                       suffix={item.suffix || ""} metric={kpis[item.key]}
                       rangeSuggestion={payload.range_suggestion}
                       onUseSupportedRange={portal.onUseSupportedRange} />
            ))}
          </div>
        </section>

        <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
          <TrendChart series={payload.series} metricKey="visits" title="Visits over time"
                      unitLabel="visits" description={payload.reporting?.range_label}
                      onChangeRange={portal.onChangeRange} />
          <FunnelChart funnel={payload.funnel} title={funnelTitle}
                       description="Server-provided stages only." />
        </div>

        {beforeGroups}

        {groups.map((group) => (
          <KpiGroup key={group.id} {...group} kpis={kpis} scopeTypes={[scopeType]}
                    rangeSuggestion={payload.range_suggestion}
                    onUseSupportedRange={portal.onUseSupportedRange} />
        ))}

        {afterGroups}

        <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
          <TrafficSourceChart sources={payload.traffic_sources}
                              onChangeRange={portal.onChangeRange} />
          <AudienceBreakdown audience={payload.audience} />
        </div>
      </div>
    </div>
  );
};
